import {
	ASSIGNMENT_FRANCHISE_TYPE_OPTIONS,
	DIVISION_RECOMMENDER_OPTIONS,
	INFRASTRUCTURE_OPTIONS,
	OWNERSHIP_OPTIONS,
	PageTypeEnum,
	TEMPLATE_FRANCHISE_FIELDS,
} from '@/constant';
import {
	GetListSearchTypes,
	GetWorkerFranchiseAssignmentListResponse,
	GetWorkerFranchiseDetailResponse,
	GetWorkerFranchiseListResponse,
	GetWorkerFranchiseLodgingListResponse,
	GetWorkerFranchiseProgramListResponse,
	GetWorkerFranchiseRecommenderInfoListResponse,
	IFranchiseCustomField,
	IFranchiseNewListItem,
	TUploadExcelFranchiseFailedItem,
	TUploadExcelFranchiseItem,
	UploadExcelUserInfo,
} from '@/services';

import { DefaultOptionType } from 'antd/es/select';
import {
	convertNationCodeToName,
	formatDate,
	formatDateStringOrUnix,
	formatNumberKR,
	generateErrorMessagesUploadExcel,
	map,
	showDataGridWithSymbol,
} from 'utils';

import { AnyObject } from 'yup';

export const formatWorkerFranchiseDetail = (
	input: GetWorkerFranchiseDetailResponse
) => {
	let customField = {} as AnyObject;

	map(input.customField, (field: IFranchiseCustomField, number: number) => {
		customField[`customField${field?.fieldNo}`] = field?.value || null;
	});

	let timeFormat = { inputFormat: 'HH:mm', outputFormat: 'HH:mm:ss' };

	return {
		...customField,
		...input,
		registDt: formatDate(input.registDt),
		updateDt: formatDate(input.updateDt),
		workStartTime: input.workStartTime
			? formatDate(input.workStartTime, timeFormat)
			: '',
		workEndTime: input.workEndTime
			? formatDate(input.workEndTime, timeFormat)
			: '',
		isAllocationRestricted: input.isAllocationRestricted ? 1 : 0,
	};
};

export const formatWorkerFranchiseRequest = (
	searchTypes: GetListSearchTypes['searchTypes']
) => {
	return searchTypes?.map(
		(e: { searchType: string; searchValue?: string | number | null }) => {
			if (['IS_ALLOCATION_RESTRICTED'].includes(e.searchType)) {
				return {
					searchType: e.searchType,
					searchValue: e.searchValue ? '1' : '0',
				};
			}
			return e;
		}
	);
};

export const formatWorkerFranchiseListItem = (
	input: GetWorkerFranchiseListResponse['content'][number],
	isDownloadExcel = false
) => {
	return {
		...input,
		id: input.franchiseId,
		franchiseTypeDetail: input.franchiseTypeDetail || '',
		franchiseRegistDt: formatDateStringOrUnix(input.franchiseRegistDt),
		franchiseUpdateDt: formatDateStringOrUnix(input.franchiseUpdateDt),
		franchiseEmployerBirthDate: formatDate(input.franchiseEmployerBirthDate),
		franchiseIsPublic: !!input.franchiseIsPublic,
		franchiseProductionScale: isDownloadExcel
			? formatNumberKR(input?.franchiseProductionScale)
			: input?.franchiseProductionScale,
		franchiseAgreementDt: formatDate(input?.franchiseAgreementDt) || '',
	};
};

export const formatWorkerFranchiseNewListItem = (
	input: IFranchiseNewListItem
) => {
	return {
		...input,
		franchiseNewPublicFranchiseId: String(
			input.franchiseNewPublicFranchiseId || ''
		),
		franchiseNewTypeDetail: input.franchiseNewTypeDetail || '',
		franchiseNewRegistDt: formatDateStringOrUnix(input.franchiseNewRegistDt),
		franchiseNewUpdateDt: formatDateStringOrUnix(
			input.franchiseNewUpdateDt || input.franchiseNewRegistDt
		),
		franchiseNewEmployerBirthDate: formatDate(
			input.franchiseNewEmployerBirthDate
		),
	};
};

export const formatWorkerFranchiseList = ({
	input,
	isDownloadExcel = false,
}: {
	input: GetWorkerFranchiseListResponse['content'];
	isDownloadExcel?: boolean;
}) => {
	return map(input, (item) =>
		formatWorkerFranchiseListItem(item, isDownloadExcel)
	);
};

export const formatWorkerFranchiseAssignmentDetail = (
	input: GetWorkerFranchiseAssignmentListResponse['content'][number]
) => {
	return {
		...input,
		birthDate: formatDate(input.birthDate),
		workStartDate: formatDate(input.workStartDate),
		workEndDate: formatDate(input.workEndDate),
		workingPeriod: showDataGridWithSymbol(
			formatDate(input.workStartDate),
			formatDate(input.workEndDate),
			'~'
		),
		assignmentFranchiseTypeLabel: ASSIGNMENT_FRANCHISE_TYPE_OPTIONS.find(
			(e) => e.value === input.assignmentFranchiseType
		)?.label,
	};
};

export const formatWorkerFranchiseAssignmentList = (
	input: GetWorkerFranchiseAssignmentListResponse['content']
) => {
	return map(input, formatWorkerFranchiseAssignmentDetail);
};

export const formatWorkerFranchiseLodgingDetail = (
	input: GetWorkerFranchiseLodgingListResponse['content'][number]
) => {
	const ownership = OWNERSHIP_OPTIONS.find(
		(e: DefaultOptionType) => e.value === input.ownershipType
	);
	const facility = INFRASTRUCTURE_OPTIONS.find(
		(e: DefaultOptionType) => e.value === input.facilityType
	);
	return {
		...input,
		ownershipTypeName: ownership?.label || '',
		facilityTypeName: facility?.label || '',
		residentCnt: input.residentCnt || 0,
	};
};

export const formatWorkerFranchiseLodgingList = (
	input: GetWorkerFranchiseLodgingListResponse['content']
) => {
	return map(input, formatWorkerFranchiseLodgingDetail);
};

export const formatWorkerFranchiseProgramDetail = (
	input: GetWorkerFranchiseProgramListResponse['content'][number]
) => {
	return {
		...input,
		startDate: formatDate(input.startDate),
		endDate: formatDate(input.endDate),
	};
};

export const formatWorkerFranchiseProgramList = (
	input: GetWorkerFranchiseProgramListResponse['content']
) => {
	return map(input, formatWorkerFranchiseProgramDetail);
};

export const formatUploadExcelFranchiseFailedItem = (
	input: TUploadExcelFranchiseFailedItem,
	customFields: DefaultOptionType[],
	userInfo: UploadExcelUserInfo
) => {
	// Map custom fields to the desired structure
	const formattedCustomFields = customFields.map((field) => ({
		label: String(field.label),
		value: field.value as keyof TUploadExcelFranchiseItem,
	}));

	// Define field label mappings
	const fieldLabelMappings: Record<string, string> = {
		publicFranchiseName: '공공형 사업자(농협)',
		email: '고용주 아이디(이메일)',
		// Add more field mappings here as needed
		// Example:
		// franchiseName: '사업자명',
		// franchiseType: '사업자 유형',
	};

	// Update template fields with custom labels
	const modifiedTemplateFields = TEMPLATE_FRANCHISE_FIELDS.map((field) =>
		fieldLabelMappings[field.value]
			? { ...field, label: fieldLabelMappings[field.value] }
			: field
	);

	// Combine modified template fields with formatted custom fields
	const allFields = [...modifiedTemplateFields, ...formattedCustomFields];

	// Generate error messages
	const reasonFailure = generateErrorMessagesUploadExcel({
		response: { errorList: input.errorList },
		templateFields: allFields,
		pageType: PageTypeEnum.FRANCHISE,
		userInfo,
	});

	// Return the formatted item
	return {
		...input.data,
		reasonFailure,
	};
};

export const formatUploadExcelFranchiseFailedList = ({
	input,
	customFields,
	userInfo,
}: {
	input: TUploadExcelFranchiseFailedItem[];
	customFields: DefaultOptionType[];
	userInfo: UploadExcelUserInfo;
}) => {
	return input.map((item) =>
		formatUploadExcelFranchiseFailedItem(item, customFields, userInfo)
	);
};

export const formatWorkerFranchiseRecommenderInfoDetail = (
	data: GetWorkerFranchiseRecommenderInfoListResponse[number]
) => {
	return {
		...data,
		birthDate: formatDate(data?.birthDate),
		typeName: DIVISION_RECOMMENDER_OPTIONS.find(
			(item) => item.value === data?.type
		)?.label,
		providerNationalName: convertNationCodeToName(data?.providerNationalCode),
	};
};

export const formatWorkerFranchiseRecommenderInfoList = (
	input: GetWorkerFranchiseRecommenderInfoListResponse
) => {
	return map(input, formatWorkerFranchiseRecommenderInfoDetail);
};
